// src/pages/attending.js
'use client';
import { useEffect, useState } from 'react';
import supabase from '../config/supabaseClient';     // use your app's client
import EventViewer from './eventviewer';
import '../components/eventviewer.css';
import { usePassport } from '../passport';
import { normalizeEvent } from '../utils/normalizeevent';


export default function AttendingEvents() {
  const { user, loading: passportLoading } = usePassport();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      if (passportLoading) return;          // wait for Passport
      if (!user) {
        setEvents([]);
        setLoading(false);
        return;
      }

      try {
        // 1) Guest rows where I’m invited by uid OR by email
        const { data: guestRows, error: guestErr } = await supabase
          .from('guests')
          .select('event_id, status')
          .or(`guest_id.eq.${user.id},guest_email.eq.${user.email}`);

        if (guestErr) throw guestErr;

        // Build status map (event_id -> status)
        const statusMap = new Map();
        (guestRows || []).forEach(r => {
          const s = r?.status || '';
          statusMap.set(
            r.event_id,
            s || 'Invited' // default if no RSVP yet
          );
        });

        const attendingIds = [...new Set((guestRows || []).map(g => g.event_id))];

        // 2) Events I host (handle both host_id and user_id)
        const { data: hostedByHostId, error: hostErrA } = await supabase
          .from('hosting')
          .select('*')
          .eq('host_id', user.id);

        if (hostErrA) throw hostErrA;

        const { data: hostedByUserId, error: hostErrB } = await supabase
          .from('hosting')
          .select('*')
          .eq('user_id', user.id);

        if (hostErrB) throw hostErrB;

        // 3) Full rows for events I’m attending (but not necessarily hosting)
        const { data: attendingEvents, error: attendErr } = attendingIds.length
          ? await supabase.from('hosting').select('*').in('id', attendingIds)
          : { data: [], error: null };

        if (attendErr) throw attendErr;

        // 4) Merge + dedupe
        const all = [
          ...(hostedByHostId || []),
          ...(hostedByUserId || []),
          ...(attendingEvents || []),
        ];

        const seen = new Set();
        const merged = [];
        for (const e of all) {
          if (!e?.id || seen.has(e.id)) continue;
          seen.add(e.id);

          const iHost = e.host_id === user.id || e.user_id === user.id;
          const rsvp = iHost ? 'Hosting' : (statusMap.get(e.id) || 'Invited');

          merged.push({
            ...e,
            rsvp_status: rsvp,
          });
        }

        // Sort by date asc (nulls last)
        merged.sort((a, b) => {
          const da = a.event_date ? new Date(a.event_date).getTime() : Infinity;
          const db = b.event_date ? new Date(b.event_date).getTime() : Infinity;
          return da - db;
        });

        // ✅ normalize before storing
        const mergedNormalized = merged.map(normalizeEvent);
        setEvents(mergedNormalized);

      } catch (err) {
        console.error('Failed to fetch attending events:', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [passportLoading, user]);

  if (passportLoading || loading) return <p>Loading your events…</p>;
  if (events.length === 0) return <p>You’re not attending any events yet.</p>;

  return (
    <div className="attending-viewer-wrapper">
      <div className="eventviewer-title-row">
        <h1 className="eventviewer-title">Your Events</h1>
      </div>

      {/* vertical stack spacing for multiple events */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {events.map((event) => (
          <EventViewer key={event.id} event={event} />
        ))}
      </div>
    </div>
  );


}
